// src/mobile_pages/const/Progress_new.jsx
import { useState, useEffect, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronDown,
  Save,
  FileText,
  LayoutTemplate
} from "lucide-react";
import TinyMCEeditor from "../../components/const/TinyMCEeditor";
import { createConstMeta } from "../../api/const";

export default function Progress_new() {
  const { id: projectId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const editorRef = useRef(null);

  // state로 넘어온 템플릿
  const templateHtml = state?.templateContent || "";
  const templateTitle = state?.templateTitle || "";
  const templateType = state?.templateType || "weekly";

  // form 상태
  const [form, setForm] = useState({
    title: templateTitle,
    type: templateType,
    content: templateHtml,
  });

  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    content: true,
  });

  // TinyMCE에 state 템플릿 강제 반영
  useEffect(() => {
    if (editorRef.current && templateHtml) {
      editorRef.current.setContent(templateHtml);
    }
  }, [templateHtml]);

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // 뒤로가기
  const handleBack = () => {
    if (window.confirm("작성 중인 내용이 저장되지 않습니다. 나가시겠습니까?")) {
      navigate(`/project/${projectId}/const/progress`);
    }
  };

  // 저장 - FormContentManager와 동일한 방식 사용
  const handleSave = async () => {
    if (!form.title.trim()) {
      alert("보고서 제목을 입력해주세요.");
      return;
    }

    setSaving(true);
    try {
      const html = editorRef.current?.getContent() || "";

      // FormContentManager와 동일한 형식으로 저장
      const payload = {
        project: projectId,
        module: "const",
        doc_type: "progress",
        layout: null,
        values: {
          ...form,
          content: html,
        },
      };

      await createConstMeta(payload);

      alert("저장되었습니다.");
      navigate(`/project/${projectId}/const/progress`);
    } catch (err) {
      console.error("저장 실패:", err);
      alert("저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  // 템플릿 선택으로 이동
  const handleTemplateSelect = () => {
    navigate(`/project/${projectId}/const/userTemplate`);
  };

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden w-full">
      <main className="w-full px-4 pt-6 pb-24">
        {/* 페이지 타이틀 */}
        <header className="mb-4">
          <p className="text-xs text-gray-500">시공관리 · 주/월간 보고서</p>
          <h1 className="text-xl font-bold text-slate-900">
            {form.type === "weekly" ? "주간" : "월간"} 보고서 작성
          </h1>
        </header>

        {/* 템플릿 선택 바로가기 */}
        <button
          type="button"
          onClick={handleTemplateSelect}
          className="w-full mb-4 flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm active:scale-[0.99] transition-transform"
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-indigo-50">
              <LayoutTemplate size={16} className="text-indigo-500" />
            </div>
            <div className="text-left min-w-0">
              <p className="text-[13px] font-medium text-slate-800">템플릿 선택</p>
              <p className="text-[11px] text-slate-500">저장된 템플릿 불러오기</p>
            </div>
          </div>
          <ChevronDown size={16} className="text-slate-400 flex-shrink-0 -rotate-90" />
        </button>

        <div className="space-y-3">
          {/* 기본 정보 섹션 */}
          <Section
            title="기본 정보"
            expanded={expandedSections.basic}
            onToggle={() => toggleSection("basic")}
            icon={<FileText size={16} className="text-blue-500" />}
          >
            <div className="space-y-3">
              {/* 보고서 제목 */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  보고서 제목 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  placeholder="보고서 제목을 입력하세요"
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm focus:border-blue-400 focus:outline-none"
                />
              </div>

              {/* 보고서 종류 */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  보고서 종류
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, type: "weekly" }))}
                    className={`py-2.5 rounded-lg text-sm font-medium border transition-colors ${form.type === "weekly"
                        ? "bg-blue-500 text-white border-blue-500"
                        : "bg-white text-slate-600 border-gray-200 hover:bg-gray-50"
                      }`}
                  >
                    주간
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, type: "monthly" }))}
                    className={`py-2.5 rounded-lg text-sm font-medium border transition-colors ${form.type === "monthly"
                        ? "bg-purple-500 text-white border-purple-500"
                        : "bg-white text-slate-600 border-gray-200 hover:bg-gray-50"
                      }`}
                  >
                    월간
                  </button>
                </div>
              </div>
            </div>
          </Section>

          {/* 보고서 내용 섹션 */}
          <Section
            title="보고서 내용"
            expanded={expandedSections.content}
            onToggle={() => toggleSection("content")}
            icon={<FileText size={16} className="text-green-500" />}
          >
            <div className="rounded-lg overflow-hidden border border-gray-200 bg-white">
              <TinyMCEeditor
                height={400}
                initialValue={templateHtml}
                editorRef={editorRef}
              />
            </div>
          </Section>
        </div>
      </main>

      {/* 모바일 하단 고정 액션바 */}
      <footer className="fixed inset-x-0 bottom-0 bg-white border-t border-gray-200 px-4 py-2.5 safe-area-bottom z-50">
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="w-full h-11 rounded-full border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 active:scale-[0.97] transition-all flex items-center justify-center gap-2"
          >
            <ChevronLeft size={18} />
            취소
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="w-full h-11 rounded-full bg-blue-500 text-white text-sm font-medium hover:bg-blue-600 active:scale-[0.97] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Save size={18} />
            {saving ? "저장 중..." : "저장"}
          </button>
        </div>
      </footer>
    </div>
  );
}

// 섹션 카드 컴포넌트
function Section({ title, expanded, onToggle, icon, children }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100"
      >
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-semibold text-sm text-gray-800">{title}</h3>
        </div>
        <ChevronDown
          size={18}
          className={`text-gray-400 transition-transform duration-200 ${expanded ? "rotate-180" : ""
            }`}
        />
      </button>
      {expanded && <div className="p-4">{children}</div>}
    </section>
  );
}
